import React from 'react';
import './Pages.css';

const DataDeletion: React.FC = () => {
  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Data Deletion & Account Closure</h1>
      </div>
      
      <div className="page-content legal-content">
        <p className="last-updated">Last updated: January 2024</p>

        <section className="content-section">
          <h2>Your Right to Delete Your Data</h2>
          <p>
            In accordance with GDPR and global privacy regulations, you have the right to 
            request deletion of your personal data at any time. We make this process simple 
            and transparent.
          </p>
        </section>

        <section className="content-section">
          <h2>How to Delete Your Data</h2>
          
          <h3>Method 1: In-App Deletion (Recommended)</h3>
          <ol>
            <li>Open the GET AI PORTRAIT app</li>
            <li>Go to Settings → Account</li>
            <li>Tap "Delete Account & Data"</li>
            <li>Confirm your decision</li>
            <li>Your data will be queued for deletion immediately</li>
          </ol>

          <h3>Method 2: Email Request</h3>
          <ol>
            <li>Send an email to <a href="mailto:privacy@dreif-ai.com">privacy@dreif-ai.com</a></li>
            <li>Use subject line: "Data Deletion Request"</li>
            <li>Include the email address associated with your account</li>
            <li>We will confirm receipt within 48 hours</li>
            <li>Deletion will be completed within 30 days</li>
          </ol>
        </section>

        <section className="content-section">
          <h2>What Data Is Deleted?</h2>
          
          <h3>Immediately Deleted:</h3>
          <ul>
            <li>All uploaded selfies and photos</li>
            <li>Generated AI portraits</li>
            <li>Face detection data and processing artifacts</li>
            <li>Personal profile information</li>
            <li>App usage data and preferences</li>
            <li>Style selections and favorites</li>
          </ul>

          <h3>Retained for Legal Compliance:</h3>
          <ul>
            <li>Transaction records (kept for 7 years for tax purposes)</li>
            <li>Data deletion request logs (proof of compliance)</li>
            <li>Legal communications if applicable</li>
          </ul>
          <p>
            <strong>Note:</strong> Retained data is anonymized and cannot be linked back to you.
          </p>
        </section>

        <section className="content-section">
          <h2>Deletion Timeline</h2>
          <ul>
            <li><strong>0-48 hours:</strong> Request acknowledged</li>
            <li><strong>1-7 days:</strong> Active data deleted from primary systems</li>
            <li><strong>7-30 days:</strong> Data removed from backups and caches</li>
            <li><strong>30 days:</strong> Confirmation email sent</li>
          </ul>
          <p>
            During this period, your account will be deactivated and inaccessible.
          </p>
        </section>

        <section className="content-section">
          <h2>Before You Delete</h2>
          <p>Please note that account deletion is permanent and will result in:</p>
          <ul>
            <li>Loss of all generated portraits (download them first!)</li>
            <li>Loss of access to purchased content</li>
            <li>Cancellation of any active subscriptions</li>
            <li>Inability to recover your account or data</li>
          </ul>
          <p>
            <strong>Recommendation:</strong> Download all portraits you wish to keep before 
            requesting deletion.
          </p>
        </section>

        <section className="content-section">
          <h2>Partial Data Deletion</h2>
          <p>
            If you want to delete specific data without closing your account:
          </p>
          <ul>
            <li><strong>Delete individual portraits:</strong> Long-press and select delete in the app</li>
            <li><strong>Clear uploaded photos:</strong> Settings → Privacy → Clear Upload History</li>
            <li><strong>Reset preferences:</strong> Settings → Reset All Preferences</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>Third-Party Services</h2>
          <p>
            If you signed in using third-party services (Apple ID, Google), you should also:
          </p>
          <ul>
            <li>Revoke app permissions in your Apple/Google account settings</li>
            <li>Remove GET AI PORTRAIT from connected apps</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>Data Portability</h2>
          <p>
            Before deleting your account, you can request a copy of all your data:
          </p>
          <ol>
            <li>Go to Settings → Privacy → Download My Data</li>
            <li>Or email <a href="mailto:privacy@dreif-ai.com">privacy@dreif-ai.com</a> with subject "Data Export Request"</li>
            <li>Receive your data in JSON format within 7 days</li>
          </ol>
        </section>

        <section className="content-section">
          <h2>Reactivation Policy</h2>
          <p>
            Once your account is deleted:
          </p>
          <ul>
            <li>Data cannot be recovered</li>
            <li>You may create a new account with the same email after 90 days</li>
            <li>Previous purchases cannot be transferred to new accounts</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>Contact Us</h2>
          <p>
            For questions about data deletion or assistance with the process:
          </p>
          <p>
            Privacy Team: <a href="mailto:privacy@dreif-ai.com">privacy@dreif-ai.com</a><br/>
            Data Protection Officer: <a href="mailto:dpo@dreif-ai.com">dpo@dreif-ai.com</a><br/>
            Response time: Within 48 hours
          </p>
        </section>

        <section className="content-section">
          <h2>Your Privacy Rights</h2>
          <p>
            Data deletion is just one of your privacy rights. You also have the right to:
          </p>
          <ul>
            <li>Access your data</li>
            <li>Correct inaccurate data</li>
            <li>Restrict processing</li>
            <li>Object to certain uses</li>
          </ul>
          <p>
            Learn more in our <a href="/privacy">Privacy Policy</a> and <a href="/gdpr">GDPR Rights</a> pages.
          </p>
        </section>
      </div>
    </div>
  );
};

export default DataDeletion;